import { PdfLayout } from '../PdfLayout';
import { Wand2, Tags, LayoutDashboard, Bell, MessageSquare } from 'lucide-react';

const benefits = [
  {
    icon: Tags,
    title: 'Automatic Tag Scanning',
    text: "Browses a device's tags and watches live values for behavior — counting up, oscillating, switching on and off.",
  },
  {
    icon: Wand2,
    title: 'Role Classification',
    text: 'Gives each tag a role — running, part count, spindle speed, temperature, position, alarm and more — with the evidence and a confidence.',
  },
  {
    icon: LayoutDashboard,
    title: 'One-Click Dashboard',
    text: "Review the results, untick what you don't want, and it creates the tag subscriptions and a monitoring dashboard.",
  },
  {
    icon: Bell,
    title: 'Suggested Alarms & Deadbands',
    text: 'Record a machine for up to 8 hours at the sample rate you choose; optional deep AI analysis suggests alarm limits, deadbands and history — tick the ones you want and apply them.',
  },
  {
    icon: MessageSquare,
    title: 'Works Air-Gapped',
    text: 'Local classification needs no AI. AI requests are relayed through your browser to Scada.Ink Cloud — the appliance never needs internet.',
  },
];

export function SmartAssist() {
  return (
    <PdfLayout
      category="Intelligence"
      headline="SCADA Software Should Tell You How Your Machine Works,"
      headlineHighlight="Not the Other Way Around"
      description="Point Smart Assist at a connected device while it's producing. It works out which tags carry production data and builds a dashboard from them — turning hours of manual configuration into minutes. With a Scada.Ink Cloud subscription, optional AI refines the results and designs custom pages from a description."
      accentColor="#7c3aed"
      benefits={benefits}
    >
      <SmartAssistMockup />
    </PdfLayout>
  );
}

function SmartAssistMockup() {
  const tags = [
    { raw: 'path_feedrate', role: 'Feed Rate', type: 'high', color: '#16a34a' },
    { raw: 'Xact', role: 'X Position', type: 'high', color: '#16a34a' },
    { raw: 'spindle_speed', role: 'Spindle Speed', type: 'high', color: '#16a34a' },
    { raw: 'part_count', role: 'Part Count', type: 'high', color: '#16a34a' },
    { raw: 'execution', role: 'Running', type: 'medium', color: '#ca8a04' },
    { raw: 'Stemp', role: 'Temperature', type: 'low', color: '#64748b' },
  ];

  return (
    <div className="glass-panel p-3 w-full max-w-[280px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-status-fault/60" />
          <div className="w-2 h-2 rounded-full bg-status-idle/60" />
          <div className="w-2 h-2 rounded-full bg-status-running/60" />
        </div>
        <span className="text-[9px] text-text-muted">Smart Assist</span>
      </div>

      {/* Device header */}
      <div className="glass-card p-2 mb-2 flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-brand-secondary/10 flex items-center justify-center">
          <Wand2 size={12} className="text-brand-secondary" />
        </div>
        <div>
          <div className="text-[8px] font-medium text-text-primary">Haas VF-2 — Analyzing</div>
          <div className="text-[7px] text-text-muted">42 tags discovered · 6 classified</div>
        </div>
      </div>

      {/* Tag classification table */}
      <div className="glass-card overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-x-2 px-2 py-1.5 bg-ink-muted text-[7px] font-medium text-text-muted border-b border-glass-border">
          <span>Tag Path</span>
          <span>Role</span>
          <span>Confidence</span>
        </div>
        {tags.map((t) => (
          <div
            key={t.raw}
            className="grid grid-cols-[1fr_1fr_auto] gap-x-2 px-2 py-1 text-[7px] border-b border-glass-border last:border-0"
          >
            <span className="text-text-muted font-mono truncate">{t.raw}</span>
            <span className="text-text-primary font-medium">{t.role}</span>
            <span
              className="px-1.5 py-0.5 rounded-full text-[6px] font-medium self-center"
              style={{ backgroundColor: `${t.color}15`, color: t.color }}
            >
              {t.type}
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-2 px-1">
        <span className="text-[8px] text-text-muted">Classifying tags...</span>
        <span className="text-[8px] text-brand-secondary font-medium">● Analyzing</span>
      </div>
    </div>
  );
}
